"use client";

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchJobDepartmentsAsync,
  selectJobDepartments,
} from "../../AdminPanel/JobPostingSlice/JobDepartment/JobDepartmentSlice";
import {
  addJobPreferenceAsync,
  fetchJobPreferencesAsync,
  selectJobPreference,
} from "../CandidateProfileSlice/JobPreference/JobPreferenceSlice";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputLeftElement,
  Spinner,
  Stack,
  Text,
  Alert,
  AlertIcon,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  FormHelperText,
} from "@chakra-ui/react";
import {
  ArrowRightIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import {
  fetchPublicCoreLocationsAsync,
  selectPublicCoreLocations,
} from "../../jobSearch/FetchLocationSlice/FetchLocationSlice";
import {
  fetchJobSkillsAsync,
  selectJobSkills,
} from "../../AdminPanel/JobPostingSlice/JobSkillsList/JobSkillsSlice";

const WorkPlace = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();

  // Selectors
  const jobPreferences = useSelector(selectJobPreference);
  const jobDepartments = useSelector(selectJobDepartments);
  const coreLocation = useSelector(selectPublicCoreLocations);
  const locationLoading = useSelector(
    (state) => state.publicCoreLocations?.loading || false
  );
  const coreSkills = useSelector(selectJobSkills);
  const skillsLoading = useSelector((state) => state.jobSkills?.loading || false);

  // State
  const [location, setLocation] = useState([]);
  const [locationId, setLocationId] = useState([]);
  const [skills, setSkills] = useState([]);
  const [skillsId, setSkillsId] = useState([]);
  const [isChecking, setIsChecking] = useState(true);
  const [locationSearchTerm, setLocationSearchTerm] = useState("");
  const [skillsSearchTerm, setSkillsSearchTerm] = useState("");

  const isMountedRef = useRef(true);

  // Disclosures
  const {
    isOpen: isLocationMenuOpen,
    onOpen: onLocationMenuOpen,
    onClose: onLocationMenuClose,
  } = useDisclosure();

  const {
    isOpen: isSkillsMenuOpen,
    onOpen: onSkillsMenuOpen,
    onClose: onSkillsMenuClose,
  } = useDisclosure();

  // Form
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      preferred_location: [],
      preferred_skills: [],
      department_uuid: "",
      job_type: "",
      employment_type: "",
      preferred_shift: "Day Shift",
      pay_type: "",
      expected_salary_currency: "INR",
      expected_salary_amount: "",
      notice_period: "",
    },
  });

  // Cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initial data fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([
          dispatch(fetchJobPreferencesAsync()),
          dispatch(fetchJobDepartmentsAsync()),
          dispatch(fetchPublicCoreLocationsAsync({ keyword: "" })),
          dispatch(fetchJobSkillsAsync()),
        ]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        if (isMountedRef.current) {
          toast({
            title: "Error loading data",
            description: "Failed to load form options. Please refresh the page.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          });
        }
      }
    };

    fetchInitialData();
    window.scrollTo(0, 0);
  }, [dispatch, toast]);

  // Check existing preferences and redirect
  useEffect(() => {
    if (jobPreferences !== null && jobPreferences !== undefined) {
      setIsChecking(false);

      // Check if job preferences already exist
      const hasPreferences =
        (Array.isArray(jobPreferences) && jobPreferences.length > 0) ||
        (typeof jobPreferences === "object" &&
          jobPreferences !== null &&
          Object.keys(jobPreferences).length > 0);

      if (hasPreferences) {
        navigate("/candidate-dashboard", { replace: true });
      }
    }
  }, [jobPreferences, navigate]);

  // Populate form with existing preferences
  useEffect(() => {
    if (jobPreferences?.preference_location) {
      const locations = jobPreferences.preference_location.map(
        (item) => item.location
      );
      setLocation(locations || []);
      setLocationId(locations.map((loc) => loc.uuid) || []);
      setValue("preferred_location", locations.map((loc) => loc.uuid) || []);
    }

    if (jobPreferences?.preference_skills) {
      const preferredSkills = jobPreferences.preference_skills.map(
        (item) => item.skill
      );
      setSkills(preferredSkills || []);
      setSkillsId(preferredSkills.map((skill) => skill.uuid) || []);
      setValue("preferred_skills", preferredSkills.map((s) => s.uuid) || []);
    }
  }, [jobPreferences, setValue]);

  // Debounced search for locations
  useEffect(() => {
    const handler = setTimeout(() => {
      if (isLocationMenuOpen) {
        dispatch(
          fetchPublicCoreLocationsAsync({ keyword: locationSearchTerm.trim() })
        );
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [locationSearchTerm, isLocationMenuOpen, dispatch]);

  // Debounced search for skills
  useEffect(() => {
    const handler = setTimeout(() => {
      if (isSkillsMenuOpen) {
        dispatch(fetchJobSkillsAsync(skillsSearchTerm.trim()));
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [skillsSearchTerm, isSkillsMenuOpen, dispatch]);

  // Location handlers
  const handleAddLocation = (selectedLocation) => {
    if (!location.some((loc) => loc.uuid === selectedLocation.uuid)) {
      const updatedLocation = [...location, selectedLocation];
      const updatedLocationId = updatedLocation.map((loc) => loc.uuid);

      setLocation(updatedLocation);
      setLocationId(updatedLocationId);
      setValue("preferred_location", updatedLocationId, {
        shouldValidate: true,
      });
    }
    onLocationMenuClose();
    setLocationSearchTerm("");
  };

  const removeLocation = (index) => {
    const updatedLocation = location.filter((_, i) => i !== index);
    const updatedLocationId = updatedLocation.map((loc) => loc.uuid);

    setLocation(updatedLocation);
    setLocationId(updatedLocationId);
    setValue("preferred_location", updatedLocationId, {
      shouldValidate: true,
    });
  };

  // Skills handlers
  const handleAddSkill = (selectedSkill) => {
    if (!skills.some((skill) => skill.uuid === selectedSkill.uuid)) {
      const updatedSkills = [...skills, selectedSkill];
      const updatedSkillIds = updatedSkills.map((skill) => skill.uuid);

      setSkills(updatedSkills);
      setSkillsId(updatedSkillIds);
      setValue("preferred_skills", updatedSkillIds, {
        shouldValidate: true,
      });
    }
    onSkillsMenuClose();
    setSkillsSearchTerm("");
  };

  const removeSkill = (index) => {
    const updatedSkills = skills.filter((_, i) => i !== index);
    const updatedSkillIds = updatedSkills.map((skill) => skill.uuid);

    setSkills(updatedSkills);
    setSkillsId(updatedSkillIds);
    setValue("preferred_skills", updatedSkillIds, {
      shouldValidate: true,
    });
  };

  // Validation
  const validateSalary = (value) => {
    if (value === undefined || value === null || value === "")
      return "Expected salary is required";
    const num = Number(value);
    if (num < 0) return "Salary cannot be negative";
    if (num < 1000) return "Salary must be at least 1,000";
    if (num > 100000000) return "Salary seems unrealistic";
    return true;
  };

  // Form submission
  const handleSave = async (formData) => {
    // Validate arrays
    if (!locationId || locationId.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one preferred location",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    if (!skillsId || skillsId.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one preferred skill",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    const submissionData = {
      preferred_location: locationId,
      preferred_skills: skillsId,
      department_uuid: formData.department_uuid,
      job_type: formData.job_type,
      employment_type: formData.employment_type,
      preferred_shift: formData.preferred_shift,
      pay_type: formData.pay_type,
      expected_salary_currency: formData.expected_salary_currency,
      expected_salary_amount: formData.expected_salary_amount,
      notice_period: formData.notice_period,
    };

    try {
      const response = await dispatch(
        addJobPreferenceAsync({ newJobPreference: submissionData })
      );

      if (!isMountedRef.current) return;

      if (addJobPreferenceAsync.fulfilled.match(response)) {
        toast({
          title: "Job Preference Saved",
          description: "Your job preferences have been successfully saved.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });

        navigate("/candidate-profile/language");
      } else {
        throw new Error("Failed to add job preference");
      }
    } catch (error) {
      if (!isMountedRef.current) return;

      console.error("Error saving job preferences:", error);
      toast({
        title: "Error",
        description:
          error.message || "Failed to save job preferences. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handlePrevious = () => {
    navigate("/candidate-profile/experience");
  };

  if (isChecking) {
    return (
      <Box
        minH="calc(100vh - 200px)"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Stack align="center" spacing={4}>
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
          <Text fontSize="lg" color="gray.600">
            Loading job preferences form...
          </Text>
        </Stack>
      </Box>
    );
  }

  return (
    <Box minH="calc(100vh - 200px)" >
      <Box
        maxW="7xl"
        mx="auto"
        bg="white"
        borderRadius="xl"
        shadow="sm"
        p={{ base: 4, md: 8 }}
      >
        <Text fontSize="2xl" fontWeight="semibold" mb={6}>
          Job Preferences
        </Text>

        <form onSubmit={handleSubmit(handleSave)}>
          <Stack spacing={6}>
            {/* Preferred Location */}
            <FormControl isInvalid={!!errors.preferred_location} isRequired>
              <FormLabel fontWeight="semibold">Preferred Location</FormLabel>
              <Input
                type="hidden"
                {...register("preferred_location", {
                  required: "Preferred location is required",
                })}
              />
              <Menu
                isOpen={isLocationMenuOpen}
                onClose={() => {
                  onLocationMenuClose();
                  setLocationSearchTerm("");
                }}
                closeOnSelect={false}
                isLazy
              >
                <MenuButton
                  as={Button}
                  variant="outline"
                  rightIcon={<ChevronDownIcon />}
                  width="100%"
                  justifyContent="space-between"
                  onClick={
                    isLocationMenuOpen ? onLocationMenuClose : onLocationMenuOpen
                  }
                  textAlign="left"
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.preferred_location ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.preferred_location
                      ? "red.500"
                      : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                  autoFocus
                >
                  Add preferred location
                </MenuButton>
                <MenuList p={2} minW="100%" maxH="320px" overflowY="auto" zIndex={10}>
                  <InputGroup mb={2}>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.500" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search locations..."
                      value={locationSearchTerm}
                      onChange={(e) => setLocationSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </InputGroup>

                  {locationLoading ? (
                    <Stack align="center" justify="center" py={4}>
                      <Spinner size="sm" />
                      <Text fontSize="sm">Loading...</Text>
                    </Stack>
                  ) : Array.isArray(coreLocation) && coreLocation.length > 0 ? (
                    coreLocation.map((item) => (
                      <MenuItem
                        key={item.uuid}
                        onClick={() => handleAddLocation(item)}
                      >
                        {item.title}
                      </MenuItem>
                    ))
                  ) : (
                    <Box px={3} py={2} color="gray.500" fontSize="sm">
                      No results found
                    </Box>
                  )}
                </MenuList>
              </Menu>
              <FormErrorMessage>
                {errors.preferred_location?.message}
              </FormErrorMessage>

              {location.length > 0 && (
                <Wrap mt={3} spacing={2}>
                  {location.map((loc, index) => (
                    <WrapItem key={index}>
                      <Tag size="lg" colorScheme="blue" borderRadius="md">
                        <TagLabel>{loc.title}</TagLabel>
                        <TagCloseButton onClick={() => removeLocation(index)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
              <FormHelperText>
                Select multiple locations where you'd prefer to work
              </FormHelperText>
            </FormControl>

            {/* Preferred Skills */}
            <FormControl isInvalid={!!errors.preferred_skills} isRequired>
              <FormLabel fontWeight="semibold">Preferred Skills</FormLabel>
              <Input
                type="hidden"
                {...register("preferred_skills", {
                  required: "Preferred skills are required",
                })}
              />
              <Menu
                isOpen={isSkillsMenuOpen}
                onClose={() => {
                  onSkillsMenuClose();
                  setSkillsSearchTerm("");
                }}
                closeOnSelect={false}
                isLazy
              >
                <MenuButton
                  as={Button}
                  variant="outline"
                  rightIcon={<ChevronDownIcon />}
                  width="100%"
                  justifyContent="space-between"
                  onClick={isSkillsMenuOpen ? onSkillsMenuClose : onSkillsMenuOpen}
                  textAlign="left"
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.preferred_skills ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.preferred_skills ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                >
                  Add preferred skills
                </MenuButton>
                <MenuList p={2} minW="100%" maxH="320px" overflowY="auto" zIndex={10}>
                  <InputGroup mb={2}>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.500" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search skills..."
                      value={skillsSearchTerm}
                      onChange={(e) => setSkillsSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </InputGroup>

                  {skillsLoading ? (
                    <Stack align="center" justify="center" py={4}>
                      <Spinner size="sm" />
                      <Text fontSize="sm">Loading...</Text>
                    </Stack>
                  ) : Array.isArray(coreSkills) && coreSkills.length > 0 ? (
                    coreSkills.map((item) => (
                      <MenuItem key={item.uuid} onClick={() => handleAddSkill(item)}>
                        {item.title}
                      </MenuItem>
                    ))
                  ) : (
                    <Box px={3} py={2} color="gray.500" fontSize="sm">
                      No results found
                    </Box>
                  )}
                </MenuList>
              </Menu>
              <FormErrorMessage>
                {errors.preferred_skills?.message}
              </FormErrorMessage>

              {skills.length > 0 && (
                <Wrap mt={3} spacing={2}>
                  {skills.map((skill, index) => (
                    <WrapItem key={index}>
                      <Tag size="lg" colorScheme="blue" borderRadius="md">
                        <TagLabel>{skill.title}</TagLabel>
                        <TagCloseButton onClick={() => removeSkill(index)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
              <FormHelperText>
                Select skills you'd like to use in your next role
              </FormHelperText>
            </FormControl>

            {/* Job Department */}
            <FormControl isInvalid={!!errors.department_uuid} isRequired>
              <FormLabel fontWeight="semibold">Job Department</FormLabel>
              <Select
                placeholder="Select department"
                {...register("department_uuid", {
                  required: "Job department is required",
                })}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={errors.department_uuid ? "red.500" : "gray.200"}
                _focus={{
                  borderColor: errors.department_uuid ? "red.500" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              >
                {Array.isArray(jobDepartments) &&
                  jobDepartments.map((job) => (
                    <option key={job.uuid} value={job.uuid}>
                      {job.title}
                    </option>
                  ))}
              </Select>
              <FormErrorMessage>
                {errors.department_uuid?.message}
              </FormErrorMessage>
            </FormControl>

            {/* Job Type */}
            <FormControl isInvalid={!!errors.job_type} isRequired>
              <FormLabel fontWeight="semibold">Job Type</FormLabel>
              <Select
                placeholder="Select job type"
                {...register("job_type", {
                  required: "Job type is required",
                })}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={errors.job_type ? "red.500" : "gray.200"}
                _focus={{
                  borderColor: errors.job_type ? "red.500" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              >
                <option value="Permanent">Permanent</option>
                <option value="Fixed-Term">Fixed-Term</option>
                <option value="Commission-Based">Commission-Based</option>
                <option value="Volunteer">Volunteer</option>
                <option value="Intern">Intern</option>
              </Select>
              <FormErrorMessage>{errors.job_type?.message}</FormErrorMessage>
            </FormControl>

            {/* Employment Type */}
            <FormControl isInvalid={!!errors.employment_type} isRequired>
              <FormLabel fontWeight="semibold">Employment Type</FormLabel>
              <Select
                placeholder="Select employment type"
                {...register("employment_type", {
                  required: "Employment type is required",
                })}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={errors.employment_type ? "red.500" : "gray.200"}
                _focus={{
                  borderColor: errors.employment_type ? "red.500" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              >
                <option value="Full Time">Full Time</option>
                <option value="Part Time">Part Time</option>
              </Select>
              <FormErrorMessage>
                {errors.employment_type?.message}
              </FormErrorMessage>
            </FormControl>

            {/* Preferred Shift */}
            <FormControl isInvalid={!!errors.preferred_shift} isRequired>
              <FormLabel fontWeight="semibold">Preferred Shift</FormLabel>
              <Select
                {...register("preferred_shift", {
                  required: "Preferred shift is required",
                })}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={errors.preferred_shift ? "red.500" : "gray.200"}
                _focus={{
                  borderColor: errors.preferred_shift ? "red.500" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              >
                <option value="Day Shift">Day Shift</option>
                <option value="Night Shift">Night Shift</option>
                <option value="Flexible">Flexible</option>
              </Select>
              <FormErrorMessage>
                {errors.preferred_shift?.message}
              </FormErrorMessage>
            </FormControl>

            {/* Expected Salary */}
            <Stack direction={{ base: "column", md: "row" }} spacing={4}>
              <FormControl
                isInvalid={!!errors.expected_salary_currency}
                isRequired
                flex="1"
              >
                <FormLabel fontWeight="semibold">Currency</FormLabel>
                <Select
                  {...register("expected_salary_currency", {
                    required: "Currency is required",
                  })}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={
                    errors.expected_salary_currency ? "red.500" : "gray.200"
                  }
                  _focus={{
                    borderColor: errors.expected_salary_currency
                      ? "red.500"
                      : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                </Select>
                <FormErrorMessage>
                  {errors.expected_salary_currency?.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl
                isInvalid={!!errors.expected_salary_amount}
                isRequired
                flex="2"
              >
                <FormLabel fontWeight="semibold">Expected Salary</FormLabel>
                <Input
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  placeholder="Enter expected salary"
                  {...register("expected_salary_amount", {
                    required: "Expected salary is required",
                    validate: validateSalary,
                  })}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={
                    errors.expected_salary_amount ? "red.500" : "gray.200"
                  }
                  _focus={{
                    borderColor: errors.expected_salary_amount
                      ? "red.500"
                      : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                />
                <FormErrorMessage>
                  {errors.expected_salary_amount?.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.pay_type} isRequired flex="1">
                <FormLabel fontWeight="semibold">Pay Type</FormLabel>
                <Select
                  placeholder="Select pay type"
                  {...register("pay_type", {
                    required: "Pay type is required",
                  })}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.pay_type ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.pay_type ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                >
                  <option value="Per Month">Per Month</option>
                  <option value="Per Year">Per Year</option>
                </Select>
                <FormErrorMessage>{errors.pay_type?.message}</FormErrorMessage>
              </FormControl>
            </Stack>

            {/* Notice Period */}
            <FormControl isInvalid={!!errors.notice_period} isRequired>
              <FormLabel fontWeight="semibold">Notice Period</FormLabel>
              <Select
                placeholder="Select notice period"
                {...register("notice_period", {
                  required: "Notice period is required",
                })}
                borderWidth="1px"
                borderRadius="lg"
                borderColor={errors.notice_period ? "red.500" : "gray.200"}
                _focus={{
                  borderColor: errors.notice_period ? "red.500" : "blue.500",
                  boxShadow: "0 0 0 1px",
                }}
              >
                <option value="Immediate Join">Immediate Join</option>
                <option value="15 Days or less">15 Days or less</option>
                <option value="1 month">1 month</option>
                <option value="2 months">2 months</option>
                <option value="3 months">3 months</option>
                <option value="More than 3 months">More than 3 months</option>
                <option value="Serving Notice Period">
                  Serving Notice Period
                </option>
              </Select>
              <FormErrorMessage>{errors.notice_period?.message}</FormErrorMessage>
            </FormControl>

            {/* Error Summary */}
            {Object.keys(errors).length > 0 && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">Please fix the following errors:</Text>
                  <Text fontSize="sm" mt={1}>
                    {Object.keys(errors).length} field(s) require attention
                  </Text>
                </Box>
              </Alert>
            )}

            {/* Navigation Buttons */}
            <Box textAlign="center" mt={8}>
              <Stack
                direction={{ base: "column", sm: "row" }}
                spacing={4}
                justifyContent="center"
              >
                <Button
                  leftIcon={<ArrowLeftIcon />}
                  colorScheme="gray"
                  onClick={handlePrevious}
                  width={{ base: "full", sm: "auto" }}
                  minW="150px"
                  isDisabled={isSubmitting}
                >
                  Previous
                </Button>
                <Button
                  rightIcon={<ArrowRightIcon />}
                  type="submit"
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  loadingText="Saving..."
                  width={{ base: "full", sm: "auto" }}
                  minW="150px"
                >
                  Next
                </Button>
              </Stack>
            </Box>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};

export default WorkPlace;
